import type { Word, Pairing, PairingStatus, ClueStatus } from '../types'
import { getLetters, getLettersFromText } from './pairings'

/**
 * Escape special regex characters except underscore (which we use as wildcard)
 */
function escapeRegexExceptUnderscore(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Search words using wildcard pattern (underscore = any character)
 * Example: "K_I_E" matches "KNIFE"
 * Searches against letters only (spaces ignored)
 */
export function searchWordsWithWildcard(words: Word[], pattern: string): Word[] {
  if (!pattern) return words

  // Escape regex special chars, then replace underscore with dot (any char)
  const escaped = escapeRegexExceptUnderscore(getLettersFromText(pattern))
  const regex = new RegExp('^' + escaped.replace(/_/g, '.') + '$', 'i')

  return words.filter(word => regex.test(getLetters(word)))
}

/**
 * Find pairings where both words have matching characters at specified positions
 * Example: findByLetterPositions(pairings, words, {1: 'N'}) finds pairs where position 1 is 'N' in both words
 * Positions are based on letters only (spaces ignored)
 */
export function findByLetterPositions(
  pairings: Pairing[],
  words: Word[],
  positions: Record<number, string>
): Pairing[] {
  const wordMap = new Map(words.map(w => [w.id, w]))

  return pairings.filter(pairing => {
    const wordA = wordMap.get(pairing.wordAId)
    const wordB = wordMap.get(pairing.wordBId)

    if (!wordA || !wordB) return false

    const lettersA = getLetters(wordA)
    const lettersB = getLetters(wordB)

    return Object.entries(positions).every(([pos, char]) => {
      const index = parseInt(pos)
      const upperChar = char.toUpperCase()
      return lettersA[index] === upperChar && lettersB[index] === upperChar
    })
  })
}

/**
 * Find all pairings that could cross another pairing at a specific position
 * This finds pairs where the letter at the crossing position matches in both words
 * Positions are based on letters only (spaces ignored)
 */
export function findCrossingPairings(
  targetPairing: Pairing,
  position: number,
  allPairings: Pairing[],
  words: Word[]
): Pairing[] {
  const wordMap = new Map(words.map(w => [w.id, w]))

  const targetWordA = wordMap.get(targetPairing.wordAId)
  const targetWordB = wordMap.get(targetPairing.wordBId)

  if (!targetWordA || !targetWordB) return []

  const targetLettersA = getLetters(targetWordA)
  const targetLettersB = getLetters(targetWordB)

  const letterA = targetLettersA[position]
  const letterB = targetLettersB[position]

  if (!letterA || !letterB) return []

  // Find pairings where any position has matching letters
  return allPairings.filter(pairing => {
    if (pairing.id === targetPairing.id) return false

    const wordA = wordMap.get(pairing.wordAId)
    const wordB = wordMap.get(pairing.wordBId)

    if (!wordA || !wordB) return false

    const lettersA = getLetters(wordA)
    const lettersB = getLetters(wordB)

    // Check if any position in this pairing has the same letters
    for (let i = 0; i < lettersA.length; i++) {
      if (lettersA[i] === letterA && lettersB[i] === letterB) {
        return true
      }
    }
    return false
  })
}

/**
 * Filter pairings by status
 */
export function filterByPairingStatus(
  pairings: Pairing[],
  statuses: PairingStatus[]
): Pairing[] {
  if (statuses.length === 0) return pairings
  return pairings.filter(p => statuses.includes(p.status))
}

/**
 * Filter pairings by clue status
 */
export function filterByClueStatus(
  pairings: Pairing[],
  statuses: (ClueStatus | 'empty')[]
): Pairing[] {
  if (statuses.length === 0) return pairings

  return pairings.filter(p => {
    if (!p.clue || !p.clue.text) {
      return statuses.includes('empty')
    }
    return statuses.includes(p.clue.status)
  })
}

/**
 * Search pairings by clue text
 */
export function searchByClueText(
  pairings: Pairing[],
  query: string
): Pairing[] {
  if (!query) return pairings

  const lowerQuery = query.toLowerCase()
  return pairings.filter(p =>
    p.clue?.text?.toLowerCase().includes(lowerQuery)
  )
}

/**
 * Find ALL possible word pair crossings at a specific position
 * This searches all words, not just existing pairings
 * Returns pairs where both words have the required letters at any position
 */
export type PossibleCrossing = {
  wordA: Word
  wordB: Word
  crossingPosition: number // position in the crossing word where letters match
  pairing?: Pairing | undefined // existing pairing if one exists
}

export function findAllPossibleCrossings(
  targetWordA: Word,
  targetWordB: Word,
  position: number,
  allWords: Word[],
  allPairings: Pairing[]
): PossibleCrossing[] {
  const targetLettersA = getLetters(targetWordA)
  const targetLettersB = getLetters(targetWordB)

  const letterA = targetLettersA[position]
  const letterB = targetLettersB[position]

  if (!letterA || !letterB) return []

  // Get all List A and List B words
  const wordsA = allWords.filter(w => w.list === 'A' && w.id !== targetWordA.id)
  const wordsB = allWords.filter(w => w.list === 'B' && w.id !== targetWordB.id)

  // Build pairing lookup map
  const pairingMap = new Map<string, Pairing>()
  for (const p of allPairings) {
    pairingMap.set(`${p.wordAId}:${p.wordBId}`, p)
  }

  const crossings: PossibleCrossing[] = []

  // Find all word A / word B combinations where letters match at some position
  for (const wordA of wordsA) {
    const lettersA = getLetters(wordA)

    for (const wordB of wordsB) {
      const lettersB = getLetters(wordB)

      // Words must be same length
      if (lettersA.length !== lettersB.length) continue

      // Check each position for matching letters
      for (let i = 0; i < lettersA.length; i++) {
        if (lettersA[i] === letterA && lettersB[i] === letterB) {
          const existingPairing = pairingMap.get(`${wordA.id}:${wordB.id}`)
          crossings.push({
            wordA,
            wordB,
            crossingPosition: i,
            pairing: existingPairing
          })
          break // Only add each word pair once
        }
      }
    }
  }

  return crossings
}

/**
 * Find ALL possible crossings for a word pair across ALL positions
 * Returns crossings grouped by which positions they can cross at
 */
export type CrossingWithPositions = {
  wordA: Word
  wordB: Word
  positions: number[] // all positions where this pair could cross
  pairing?: Pairing | undefined
}

export function findAllCrossingsForPair(
  targetWordA: Word,
  targetWordB: Word,
  allWords: Word[],
  allPairings: Pairing[]
): CrossingWithPositions[] {
  const targetLettersA = getLetters(targetWordA)
  const targetLettersB = getLetters(targetWordB)

  // Get all List A and List B words (excluding targets)
  const wordsA = allWords.filter(w => w.list === 'A' && w.id !== targetWordA.id)
  const wordsB = allWords.filter(w => w.list === 'B' && w.id !== targetWordB.id)

  // Build pairing lookup map
  const pairingMap = new Map<string, Pairing>()
  for (const p of allPairings) {
    pairingMap.set(`${p.wordAId}:${p.wordBId}`, p)
  }

  const crossingsMap = new Map<string, CrossingWithPositions>()

  // For each position in the target pair
  for (let targetPos = 0; targetPos < targetLettersA.length; targetPos++) {
    const letterA = targetLettersA[targetPos]
    const letterB = targetLettersB[targetPos]

    if (!letterA || !letterB) continue

    // Find all word pairs that have these letters at ANY position
    for (const wordA of wordsA) {
      const lettersA = getLetters(wordA)

      for (const wordB of wordsB) {
        const lettersB = getLetters(wordB)

        // Words must be same length
        if (lettersA.length !== lettersB.length) continue

        // Check if this pair has the matching letters at any position
        for (let i = 0; i < lettersA.length; i++) {
          if (lettersA[i] === letterA && lettersB[i] === letterB) {
            const key = `${wordA.id}:${wordB.id}`
            const existing = crossingsMap.get(key)

            if (existing) {
              if (!existing.positions.includes(targetPos)) {
                existing.positions.push(targetPos)
              }
            } else {
              crossingsMap.set(key, {
                wordA,
                wordB,
                positions: [targetPos],
                pairing: pairingMap.get(key)
              })
            }
            break // Only count this pair once per position
          }
        }
      }
    }
  }

  return Array.from(crossingsMap.values())
}
